import React, { useRef } from "react";
import clsx from "clsx";
import styles from "./Summary.module.scss";
import Button from "../../../../../../shared-components/css-modules/toyota/atoms/Button";
import { useCarConfigLabel } from "../../../../../utils/labelUtils";
import { summaryTabVariants, useTabsContext } from "./SummaryTabsContext";
import Icon from "../../../../../../shared-components/css-modules/toyota/atoms/Icon";
import type { SummaryTabType } from "../../../../../utils/types/SummaryType";
import useAphSummaryTabs from "../../../../../hooks/useAphSummaryTabs";
import { trackOrderOverviewTab } from "../../../../../utils/tracking";

const SummaryTabs = (): JSX.Element => {
    const tabsContext = useTabsContext();
    const tabContainer = useRef<HTMLDivElement>(null);
    const tabList = useRef<HTMLUListElement>(null);
    const {
        onTabSwitch,
        onNextTab,
        onPreviousTab,
        onScroll,
        isNextTabEnabled,
        isPreviousTabEnabled,
        tabs,
        canScroll,
        canScrollLeft,
        canScrollRight,
    } = useAphSummaryTabs({ ...tabsContext, tabContainer, tabList, tabVariants: summaryTabVariants });

    const [
        carConfigOverviewTitleLabel,
        carConfigCashPurchaseLabel,
        carConfigFinanceOptionLabel,
        carConfigInsuranceTabLabel,
        carConfigBusinessLeasingTabLabel,
    ] = useCarConfigLabel([
        "carConfigOverviewTitle",
        "carConfigCashPurchase",
        "carConfigFinanceOptions",
        "carConfigInsuranceTab",
        "carConfigBusinessLeasingTab",
    ]);

    const getLabel = (tab: SummaryTabType): string => {
        const labelDictionary: Record<SummaryTabType, string> = {
            overview: carConfigOverviewTitleLabel,
            cash: carConfigCashPurchaseLabel,
            finance: carConfigFinanceOptionLabel,
            insurance: carConfigInsuranceTabLabel,
            business: carConfigBusinessLeasingTabLabel,
        };
        return labelDictionary[tab];
    };

    const [ariaSummaryTabNext, ariaSummaryTabPrevious] = useCarConfigLabel([
        "carConfigA11yButtonTabNext",
        "carConfigA11yButtonTabPrevious",
    ]);

    return (
        <div
            ref={tabContainer}
            className={clsx(
                styles["tabs-container"],
                canScroll && styles["has-tab-navigation"],
                canScrollLeft && styles["scrollable-left"],
                canScrollRight && styles["scrollable-right"],
            )}
        >
            {canScroll && (
                <div className={styles["tabs-actions"]}>
                    <button
                        className={styles["tabs-nav-button"]}
                        onClick={onPreviousTab}
                        type="button"
                        disabled={!isPreviousTabEnabled}
                        aria-label={ariaSummaryTabPrevious}
                    >
                        <Icon variant="chevron-left" sizing="sm" aria-hidden="true" />
                    </button>
                    <button
                        className={styles["tabs-nav-button"]}
                        onClick={onNextTab}
                        type="button"
                        disabled={!isNextTabEnabled}
                        aria-label={ariaSummaryTabNext}
                    >
                        <Icon variant="chevron-right" sizing="sm" aria-hidden="true" />
                    </button>
                </div>
            )}
            <ul className={styles.tabs} role="tablist" ref={tabList} onScroll={onScroll}>
                {tabs.map((tab, index) => (
                    <li role="presentation" key={`tab-${summaryTabVariants[tab]}`}>
                        <Button
                            variant="clean"
                            role="tab"
                            id={`tab-${summaryTabVariants[tab]}`}
                            aria-selected={
                                tabsContext.openTab === tab || (tabsContext.openTab === undefined && index === 0)
                            }
                            title={getLabel(tab)}
                            className={styles.tab}
                            onClick={(event) => {
                                event.preventDefault();
                                trackOrderOverviewTab(tab);
                                onTabSwitch(tab);
                            }}
                        >
                            {getLabel(tab)}
                        </Button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

export default SummaryTabs;
